import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { BarChart } from '@/components/charts/BarChart'
import { ColumnChart } from '@/components/charts/ColumnChart'
import { StatTile } from '@/components/charts/StatTile'
import { CaptureList } from '@/components/jobs/CaptureList'
import { STATUS_LABELS } from '@/components/judgments/StatusBadge'
import { PageHeader } from '@/components/layout/PageHeader'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Skeleton } from '@/components/ui/Spinner'
import { useCaptures } from '@/hooks/useJobs'
import { useStats } from '@/hooks/useJudgments'
import { EMPTY, formatNumber, titleCase } from '@/lib/format'
import type { JudgmentStatus } from '@/types/api'

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
    <path
      d="M4 20V10M10 20V4M16 20v-7M22 20H2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
)

/** The page's own shape, drawn while the numbers load. */
function DashboardSkeleton() {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-26 w-full" />
        ))}
      </div>
      <Skeleton className="h-64 w-full" />
      <div className="grid gap-4 lg:grid-cols-5">
        <Skeleton className="h-56 w-full lg:col-span-2" />
        <Skeleton className="h-56 w-full lg:col-span-3" />
      </div>
    </>
  )
}

export function DashboardPage() {
  const stats = useStats()
  const captures = useCaptures()
  const navigate = useNavigate()

  const statusData = useMemo(
    () =>
      (stats.data?.byStatus ?? []).map((facet) => ({
        key: facet.value,
        label: STATUS_LABELS[facet.value as JudgmentStatus] ?? facet.label,
        value: facet.count,
      })),
    [stats.data],
  )

  const yearData = useMemo(
    () =>
      (stats.data?.byYear ?? []).map((facet) => ({
        key: facet.value,
        label: facet.value,
        value: facet.count,
      })),
    [stats.data],
  )

  const locationData = useMemo(
    () =>
      (stats.data?.byLocation ?? []).map((facet) => ({
        key: facet.value,
        label: titleCase(facet.value),
        value: facet.count,
      })),
    [stats.data],
  )

  const header = (
    <PageHeader
      title="Dashboard"
      description="What is stored, and how much of it the portal was willing to show."
      actions={
        <Button variant="secondary" onClick={() => navigate('/ingest')}>
          Add records
        </Button>
      }
    />
  )

  if (stats.isLoading) {
    return (
      <>
        {header}
        <DashboardSkeleton />
      </>
    )
  }

  if (stats.isError) {
    return (
      <>
        {header}
        <Card>
          <ErrorState
            error={stats.error}
            fallbackTitle="Could not load the dashboard"
            onRetry={() => void stats.refetch()}
          />
        </Card>
      </>
    )
  }

  const data = stats.data
  if (!data || data.totalJudgments === 0) {
    return (
      <>
        {header}
        <Card>
          <EmptyState
            icon={<ChartIcon />}
            title="No data yet"
            description="Once an ingestion job succeeds, this page summarises what was collected."
            actions={
              <Button variant="primary" onClick={() => navigate('/ingest')}>
                Go to Ingest
              </Button>
            }
          />
        </Card>
      </>
    )
  }

  const detailShare =
    data.totalJudgments > 0 ? data.detailedJudgments / data.totalJudgments : 0

  // Only claim completeness when the capture list is loaded and actually says
  // so. `cappedCaptures` alone cannot support the claim.
  const captureRows = captures.data
  const failedCaptures =
    captureRows?.filter((capture) => !capture.isComplete && !capture.isCapped).length ?? 0
  const captureHint = !captureRows
    ? `${formatNumber(data.totalCaptures)} recorded`
    : data.cappedCaptures === 0 && failedCaptures === 0
      ? 'All complete'
      : [
          data.cappedCaptures > 0 ? `${data.cappedCaptures} hit the 300-record cap` : null,
          failedCaptures > 0 ? `${failedCaptures} did not finish` : null,
        ]
          .filter(Boolean)
          .join(' · ')
  const captureTone =
    failedCaptures > 0 || data.cappedCaptures > 0 ? 'warning' : 'default'

  return (
    <>
      {header}

      {data.cappedCaptures > 0 ? (
        <Alert
          tone="warning"
          title="Some source searches were truncated"
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate('/ingest')}>
              Review captures
            </Button>
          }
        >
          {data.cappedCaptures} of {data.totalCaptures} captures hit the portal&rsquo;s
          300-record display cap. Figures below describe what is stored, which is
          knowingly less than what exists.
        </Alert>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Judgments"
          value={formatNumber(data.totalJudgments)}
          hint={`${formatNumber(data.distinctNames)} distinct names · ${formatNumber(data.totalParties)} party rows`}
        />
        <StatTile
          label="Date range"
          value={
            data.earliestYear && data.latestYear
              ? `${data.earliestYear}–${data.latestYear}`
              : EMPTY
          }
          hint="The docket holds nothing before 1990."
        />
        <StatTile
          label="With docket & amount"
          value={`${Math.round(detailShare * 100)}%`}
          share={detailShare}
          hint={`${formatNumber(data.detailedJudgments)} of ${formatNumber(data.totalJudgments)} detail pages read`}
        />
        <StatTile
          label="Captures"
          value={formatNumber(data.totalCaptures)}
          tone={captureTone}
          hint={captureHint}
        />
      </div>

      <Card>
        <CardBody>
          <ColumnChart
            title="Judgments by year entered"
            subtitle="From the portal's Judgment File Date."
            data={yearData}
            valueHeading="Judgments"
          />
        </CardBody>
      </Card>

      {/* 2:3 rather than 1:1. Status has six categories and county has ten, so
          an even split leaves a visible void beside the shorter chart. */}
      {/* Stretched rather than top-aligned: "By status" has three to six
          categories and "By county" has ten, so aligning to the top left a
          visible hole beside the shorter chart. Filling the card reads as
          padding; leaving the grid cell empty read as a bug. */}
      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="flex flex-col lg:col-span-2">
          <CardBody className="flex-1">
            <BarChart
              title="By status"
              subtitle="The five statuses partition the set exactly."
              data={statusData}
              categoryHeading="Status"
              valueHeading="Judgments"
              maxRows={6}
            />
          </CardBody>
        </Card>

        <Card className="flex flex-col lg:col-span-3">
          <CardBody className="flex-1">
            <BarChart
              title="By county"
              subtitle="Where the judgment was filed."
              data={locationData}
              categoryHeading="County"
              valueHeading="Judgments"
              maxRows={10}
            />
          </CardBody>
        </Card>
      </div>

      <CaptureList
        captures={captures.data ?? []}
        onFilterByCapture={(captureId) => navigate(`/?captureId=${captureId}`)}
      />
    </>
  )
}
